import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { getVanillaEl } from "../../domBridge";
import { useAppState } from "../../hooks/useAppState";
import { appState } from "../../../state";
import { apiFetch, getCsrfToken } from "../../../api";
import { requestLock } from "../../../lockBus";
import { loadAlbums } from "../../../albumsApi";
import { loadGalleryData } from "../../../galleryApi";
import { getAutolockBridge } from "../../../autolockBridge";
import { registerUploadBridge } from "../../../uploadBridge";
import { useLockState } from "../../hooks/useLockState";

type StatusKind = "info" | "success" | "error";

function resetInactivityTimer(): void {
  getAutolockBridge()?.resetInactivityTimer();
}

function isFileDrag(e: DragEvent): boolean {
  return !!e.dataTransfer && Array.from(e.dataTransfer.types).includes("Files");
}

function isLocked(): boolean {
  return document.getElementById("contentWrap")?.classList.contains("fade-lock") ?? false;
}

export function UploadWidget() {
  const navigate = useNavigate();
  const lockState = useLockState();
  const albums = useAppState((s) => s.albums);
  const [visible, setVisible] = useState(false);
  const [label, setLabel] = useState("Uploading…");
  const [pct, setPct] = useState(0);
  const [status, setStatus] = useState<{ text: string; kind: StatusKind; albumId?: string } | null>(null);

  const pendingUploadAlbumId = useRef("all");
  const dragCounter = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (lockState === "locked") setVisible(false);
  }, [lockState]);

  function hideBox(): void {
    setVisible(false);
    setStatus(null);
    setLabel("Uploading…");
    setPct(0);
  }

  function showBox(): void {
    setVisible(true);
  }

  // Destination is already known by the time this is called - AddScreen.tsx
  // (the "/add" page) is what asks the user to choose an album now, in
  // place of the old pickAlbum() modal step this used to await here. The
  // native OS file picker itself is unchanged.
  function openFilePickerForAlbum(albumId: string): void {
    pendingUploadAlbumId.current = albumId;

    appState.filePickerOpen = true;
    resetInactivityTimer();

    if (appState.pickerTimeoutId) clearTimeout(appState.pickerTimeoutId);
    appState.pickerTimeoutId = setTimeout(() => {
      appState.filePickerOpen = false;
      appState.pickerTimeoutId = null;
    }, 90_000);

    inputRef.current?.click();
  }

  useEffect(() => {
    registerUploadBridge({ openFilePickerForAlbum });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function xhrUpload(url: string, formData: FormData): Promise<boolean> {
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url, true);
      xhr.withCredentials = true;

      const csrf = getCsrfToken();
      if (csrf) xhr.setRequestHeader("X-CSRF-Token", csrf);

      appState.uploadInProgress = true;
      resetInactivityTimer();

      xhr.upload.onprogress = (evt) => {
        resetInactivityTimer();
        if (!evt.lengthComputable) return;
        setPct(Math.round((evt.loaded / evt.total) * 100));
      };

      xhr.onerror = () => {
        appState.uploadInProgress = false;
        setStatus({ text: "Upload failed. Try again.", kind: "error" });
        resolve(false);
      };

      xhr.onload = async () => {
        appState.uploadInProgress = false;

        if (xhr.status === 401) {
          try {
            await apiFetch("/api/logout", { method: "POST" });
          } catch {
            // ignore
          }
          requestLock("unauthorized");
          resolve(false);
          return;
        }

        if (xhr.status >= 200 && xhr.status < 300) {
          setPct(100);
          // Video playback audit, Critical #3: the server heuristically
          // scans uploaded video for HEVC (server.js's detectHevc()) -
          // plays fine on iOS, but frequently doesn't decode at all on
          // Android/desktop Chrome/Firefox. Surfacing that here, at the
          // moment it's most actionable, rather than only discovering it
          // later as a silent "can't play this video" from the partner.
          let hevcWarning = false;
          try {
            const parsed = JSON.parse(xhr.responseText || "{}");
            hevcWarning = !!parsed.hevcWarning;
          } catch {
            // Success path doesn't depend on parsing the body - a missing/
            // malformed hevcWarning field just means no extra note shown.
          }
          // Destination-aware success copy - the Add page's whole premise
          // is "choose where this memory belongs," so confirming where it
          // landed (rather than a generic "Upload complete") closes that
          // loop. Falls back to the generic message for "all" (not a real
          // named destination) or if the id can't be resolved.
          const destinationAlbum = pendingUploadAlbumId.current !== "all" ? albums.find((a) => a.id === pendingUploadAlbumId.current) : null;
          const destinationText = hevcWarning
            ? "one or more videos use HEVC compression, which may not play for your partner on Android or older devices."
            : "";
          setStatus({
            text: destinationAlbum ? `Added to ${destinationAlbum.name} ✅ ${destinationText}`.trim() : `Upload complete ✅ ${destinationText}`.trim(),
            kind: "success",
            albumId: destinationAlbum?.id,
          });
          await loadAlbums();
          await loadGalleryData();
          setTimeout(hideBox, hevcWarning ? 5000 : 2000);
          resolve(true);
          return;
        }

        // A 413 with no parseable JSON body almost always means a reverse
        // proxy/CDN in front of the server (e.g. Cloudflare) rejected the
        // request before it ever reached the app - the app's own limits
        // are enforced server-side and return a JSON error, not this.
        if (xhr.status === 413) {
          setStatus({
            text: "File too large for the server to accept. This is usually a proxy/CDN upload limit (e.g. Cloudflare) rather than the app itself - try a smaller file.",
            kind: "error",
          });
          resolve(false);
          return;
        }

        let detail = "";
        try {
          const parsed = JSON.parse(xhr.responseText || "{}");
          detail = parsed.message || parsed.error || "";
        } catch {
          // Non-JSON response (e.g. an HTML error page from a proxy) -
          // don't dump raw markup into the UI.
          detail = "";
        }

        setStatus({ text: `Upload failed (${xhr.status}) ${detail ? "— " + detail : ""}`, kind: "error" });
        resolve(false);
      };

      xhr.send(formData);
    });
  }

  async function handleFiles(files: File[], albumId: string): Promise<void> {
    if (!files.length) return;

    const tooBig = files.find((f) => {
      const isVideo = /\.(mp4|webm|mov|avi)$/i.test(f.name);
      const maxSize = isVideo ? 500 * 1024 * 1024 : 10 * 1024 * 1024;
      return f.size > maxSize;
    });

    if (tooBig) {
      showBox();
      setPct(0);
      setStatus({ text: "One file is too large (max 10MB for images, 500MB for videos).", kind: "error" });
      return;
    }

    showBox();
    setPct(0);
    setLabel(`Uploading ${files.length} file(s)…`);
    setStatus(null);

    const fd = new FormData();
    files.forEach((f) => fd.append("media", f));
    if (albumId !== "all") fd.append("albumId", albumId);

    await xhrUpload("/api/upload", fd);
  }

  useEffect(() => {
    const dropOverlay = getVanillaEl<HTMLDivElement>("dropOverlay");
    if (!dropOverlay) return;

    const onDragEnter = (e: DragEvent) => {
      if (isLocked() || !isFileDrag(e)) return;
      e.preventDefault();
      dragCounter.current++;
      dropOverlay.classList.add("is-active");
    };
    const onDragOver = (e: DragEvent) => {
      if (isLocked() || !isFileDrag(e)) return;
      e.preventDefault();
    };
    const onDragLeave = (e: DragEvent) => {
      if (isLocked() || !isFileDrag(e)) return;
      dragCounter.current = Math.max(0, dragCounter.current - 1);
      if (dragCounter.current === 0) dropOverlay.classList.remove("is-active");
    };
    const onDrop = async (e: DragEvent) => {
      if (isLocked() || !isFileDrag(e)) return;
      e.preventDefault();
      dragCounter.current = 0;
      dropOverlay.classList.remove("is-active");

      const files = Array.from(e.dataTransfer?.files || []);
      await handleFiles(files, appState.currentAlbum);
    };

    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("drop", onDrop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onInputChange(): Promise<void> {
    appState.filePickerOpen = false;
    if (appState.pickerTimeoutId) {
      clearTimeout(appState.pickerTimeoutId);
      appState.pickerTimeoutId = null;
    }

    resetInactivityTimer();

    const files = Array.from(inputRef.current?.files || []);
    await handleFiles(files, pendingUploadAlbumId.current);
    if (inputRef.current) inputRef.current.value = "";
  }

  const container = getVanillaEl<HTMLDivElement>("uploadSection");
  if (!container) return null;

  return createPortal(
    <>
      <div className="d-flex align-items-center justify-content-end gap-2">
        <input
          ref={inputRef}
          type="file"
          // Matches server.js's ALLOWED_MEDIA_EXT for the gallery upload
          // endpoint exactly (jpg/jpeg/png/webp/gif + mp4/mov) - svg and
          // webm used to be advertised here but the server rejects both,
          // so picking either surfaced a confusing "Upload failed (400)"
          // for a type the picker itself just said was fine.
          accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,video/mp4,video/mov"
          multiple
          hidden
          aria-label="Select media to upload"
          onClick={() => {
            appState.filePickerOpen = true;
            resetInactivityTimer();
          }}
          onChange={onInputChange}
        />
        <button
          type="button"
          className="btn btn-pink d-none d-md-inline-flex"
          aria-label="Add media to gallery"
          onClick={() => navigate("/add")}
        >
          <i className="bi bi-plus-lg me-1" aria-hidden="true" />
          Add Media
        </button>
      </div>

      {/* A compact floating toast, not an in-flow panel - #uploadSection
          (this portal's anchor) sits unconditionally above every tab's
          content in public/index.html, so an in-flow "mt-3" block here
          used to visibly push down Home/Albums/every other page's content
          for the whole duration of an upload, worse on failure since it
          then just sat there. Fixed-position + its own z-index means it
          overlays instead of displacing anything. */}
      {visible && (
        <div className={`upload-toast${status?.kind === "error" ? " is-error" : ""}`} role="status" aria-live="polite">
          {!status || status.kind !== "error" ? (
            <>
              <div className="upload-toast-row">
                <i className="bi bi-cloud-arrow-up upload-toast-icon" aria-hidden="true" />
                <span className="upload-toast-label text-truncate">{status?.kind === "success" ? status.text : label}</span>
                <span className="upload-toast-pct">{pct}%</span>
              </div>
              <div className="upload-toast-bar" role="progressbar" aria-label="Upload progress" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                <div className={`upload-toast-bar-fill${status?.kind === "success" ? " is-success" : ""}`} style={{ width: `${pct}%` }} />
              </div>
              {status?.kind === "success" && status.albumId && (
                <button
                  type="button"
                  className="upload-toast-link"
                  onClick={() => {
                    hideBox();
                    navigate(`/albums/${status.albumId}`);
                  }}
                >
                  View album
                </button>
              )}
            </>
          ) : (
            <div className="upload-toast-row">
              <i className="bi bi-exclamation-circle-fill upload-toast-icon is-error" aria-hidden="true" />
              <span className="upload-toast-label text-truncate">{status.text}</span>
              <button type="button" className="upload-toast-retry" onClick={hideBox}>
                Dismiss
              </button>
            </div>
          )}
        </div>
      )}
    </>,
    container,
  );
}
